import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Stack, CircularProgress, IconButton } from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useSnackbar } from "notistack";
import {
  MdLocalFireDepartment,
  MdWarning,
  MdDirectionsRun,
  MdShield,
  MdCrisisAlert,
  MdDirectionsCar,
  MdBroadcastOnHome,
  MdNotifications,
  MdChatBubble,
  MdLock,
  MdPhone,
  MdSearch,
  MdWater,
} from "react-icons/md";
import {
  GiPistolGun,
  GiBoltBomb,
  GiMedicalPack,
  GiAmbulance,
  GiHeavyLightning,
  GiGasMask,
  GiHazardSign,
} from "react-icons/gi";

import Dialog from "../../../components/dialog/Dialog";
import AddressSearchField from "../../../components/settings/AddressSearchField";
import GeofenceDrawMap from "../../../components/settings/GeofenceDrawMap";
import { useGeofence, useUpsertGeofence } from "../../../hooks/useGeofence";
import {
  GetAlarmButtonsByOrganisationId,
  DeleteAlarmButton,
} from "../../../hooks/useAlarmButtonData";
import { CreateAlarmButtonModal } from "../../../modals/general/CreateAlarmButtonModal";
import { EditAlarmButtonModal } from "../../../modals/general/EditAlarmButtonModal";
import {
  INK,
  INK_MUTED,
  LINE,
  LINE_SOFT,
  BRAND,
  PrimaryButton,
} from "./orgTabsKit";
import "../../../styles/settings/geofence.css";

const ICON_COMPONENT = {
  pistol: GiPistolGun,
  bomb: GiBoltBomb,
  fire: MdLocalFireDepartment,
  alert: MdWarning,
  "medical-bag": GiMedicalPack,
  ambulance: GiAmbulance,
  "run-fast": MdDirectionsRun,
  shield: MdShield,
  "account-alert": MdCrisisAlert,
  car: MdDirectionsCar,
  "home-alert": MdBroadcastOnHome,
  bell: MdNotifications,
  "chat-alert": MdChatBubble,
  lock: MdLock,
  phone: MdPhone,
  magnify: MdSearch,
  "weather-lightning": GiHeavyLightning,
  water: MdWater,
  "gas-cylinder": GiGasMask,
  "hazard-lights": GiHazardSign,
};

const AlarmIcon = ({ name, size = 22, color }) => {
  const Comp = ICON_COMPONENT[name];
  return Comp ? <Comp size={size} color={color} /> : null;
};

const SectionHeading = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: "11px",
        background: "rgba(138,17,37,0.08)",
        color: BRAND,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
    </Box>
    <Box>
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "1.15rem",
          fontWeight: 600,
          color: INK,
          lineHeight: 1.25,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "13.5px",
          color: INK_MUTED,
          fontFamily: '"DM Sans", sans-serif',
          mt: 0.25,
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const Card = ({ children, sx }) => (
  <Box
    sx={{
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "16px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const GeofenceSection = ({ organisationId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: saved, isLoading } = useGeofence(organisationId);
  const upsert = useUpsertGeofence();

  const [address, setAddress] = useState("");
  const [center, setCenter] = useState(null);
  const [polygon, setPolygon] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!saved) return;
    setAddress(saved.address ?? "");
    if (saved.center_lat != null && saved.center_lng != null) {
      setCenter({ lat: saved.center_lat, lng: saved.center_lng });
    }
    setPolygon(saved.polygon ?? null);
    setDirty(false);
  }, [saved]);

  const hasPolygon = Boolean(polygon?.coordinates?.[0]?.length >= 4);
  const saving = upsert.isLoading;

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({
        organisationId,
        address: address || null,
        center,
        polygon,
      });
      enqueueSnackbar("Platsområde sparat.", { variant: "success" });
      setDirty(false);
    } catch (err) {
      enqueueSnackbar("Kunde inte spara platsområde.", { variant: "error" });
    }
  };

  return (
    <Box>
      <SectionHeading
        icon={PlaceRoundedIcon}
        title="Platsområde"
        subtitle="Rita det område som räknas till organisationens adress. Vid larm jämförs användarnas position mot området."
      />
      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "stretch",
        }}
      >
        <Card sx={{ p: 3, width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: INK_MUTED,
              mb: 1,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            Adress
          </Typography>
          <AddressSearchField
            value={address}
            onSelect={(opt) => {
              setAddress(opt.label);
              setCenter({ lat: opt.lat, lng: opt.lng });
              setDirty(true);
            }}
          />

          <Box sx={{ mt: 3 }}>
            <Box
              component="button"
              type="button"
              onClick={handleSave}
              disabled={!hasPolygon || saving || isLoading || !dirty}
              sx={{
                width: "100%",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 1.5,
                background: BRAND,
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 600,
                cursor:
                  !hasPolygon || saving || isLoading || !dirty
                    ? "not-allowed"
                    : "pointer",
                opacity:
                  !hasPolygon || saving || isLoading || !dirty ? 0.4 : 1,
                transition: "all 0.25s ease",
                "&:hover":
                  !hasPolygon || saving || isLoading || !dirty
                    ? {}
                    : { background: "#6b0d1d" },
              }}
            >
              {saving ? (
                <CircularProgress size={15} sx={{ color: "#fff" }} />
              ) : (
                <SaveRoundedIcon sx={{ fontSize: 17 }} />
              )}
              {saved ? "Uppdatera område" : "Spara område"}
            </Box>
            {!hasPolygon && (
              <Typography
                sx={{
                  mt: 1.25,
                  fontSize: "12px",
                  color: INK_MUTED,
                  fontFamily: '"DM Sans", sans-serif',
                  lineHeight: 1.5,
                }}
              >
                Sök en adress och rita sedan området på kartan för att kunna
                spara.
              </Typography>
            )}
            {saved?.updated_at && (
              <Typography
                sx={{
                  mt: 1.25,
                  fontSize: "11.5px",
                  color: INK_MUTED,
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                Senast sparat{" "}
                {new Date(saved.updated_at).toLocaleString("sv-SE")}
              </Typography>
            )}
          </Box>
        </Card>

        <Box
          sx={{
            flex: 1,
            minHeight: { xs: 380, md: 480 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <GeofenceDrawMap
            center={center}
            polygon={polygon}
            onChange={(next) => {
              setPolygon(next);
              setDirty(true);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const AlarmButtonRow = ({ button, onEdit, onDelete }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      px: 3,
      py: 2,
      borderBottom: `1px solid ${LINE_SOFT}`,
    }}
  >
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: "11px",
        background: button.color,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AlarmIcon name={button.icon} size={21} color="#fff" />
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "15px",
          fontWeight: 600,
          color: INK,
        }}
      >
        {button.label}
      </Typography>
      <Typography
        sx={{
          fontSize: "12px",
          color: INK_MUTED,
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        {button.icon}
      </Typography>
    </Box>
    <Stack direction="row" spacing={0.5}>
      <IconButton
        size="small"
        onClick={() => onEdit(button)}
        sx={{ color: INK_MUTED, "&:hover": { color: INK } }}
      >
        <EditRoundedIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onDelete(button)}
        sx={{ color: INK_MUTED, "&:hover": { color: BRAND } }}
      >
        <DeleteOutlineRoundedIcon fontSize="small" />
      </IconButton>
    </Stack>
  </Box>
);

const AlarmButtonsSection = ({ organisationId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = GetAlarmButtonsByOrganisationId(organisationId);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const buttons = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data]
  );

  const deleteMutation = DeleteAlarmButton({
    onSuccess: () =>
      enqueueSnackbar("Larmknapp borttagen.", { variant: "success" }),
    onError: () =>
      enqueueSnackbar("Kunde inte ta bort larmknapp.", { variant: "error" }),
  });

  const handleDelete = (button) => {
    if (window.confirm(`Ta bort larmknappen "${button.label}"?`)) {
      deleteMutation.mutate(button.id);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        <SectionHeading
          icon={NotificationsActiveRoundedIcon}
          title="Larmknappar"
          subtitle="Larmknapparna som visas i appen för organisationens användare."
        />
        <PrimaryButton
          label="Ny larmknapp"
          onClick={() => setCreateOpen(true)}
        />
      </Box>

      {isLoading ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress size={22} sx={{ color: BRAND }} />
        </Card>
      ) : buttons.length === 0 ? (
        <Card
          sx={{
            p: 5,
            textAlign: "center",
            borderStyle: "dashed",
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: "1.05rem",
              fontWeight: 600,
              color: INK,
              mb: 0.5,
            }}
          >
            Inga larmknappar ännu
          </Typography>
          <Typography
            sx={{
              fontSize: "13.5px",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            Skapa den första larmknappen för organisationen.
          </Typography>
        </Card>
      ) : (
        <Card sx={{ overflow: "hidden", "& > *:last-child": { borderBottom: "none" } }}>
          {buttons.map((button) => (
            <AlarmButtonRow
              key={button.id}
              button={button}
              onEdit={setEditTarget}
              onDelete={handleDelete}
            />
          ))}
        </Card>
      )}

      <Dialog
        title="Ny larmknapp"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      >
        <CreateAlarmButtonModal
          organisationId={organisationId}
          onClose={() => setCreateOpen(false)}
        />
      </Dialog>

      {editTarget && (
        <Dialog
          title="Redigera larmknapp"
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
        >
          <EditAlarmButtonModal
            button={editTarget}
            onClose={() => setEditTarget(null)}
          />
        </Dialog>
      )}
    </Box>
  );
};

export const OrgSettingsTab = ({ organisationId }) => (
  <Stack spacing={5}>
    <GeofenceSection organisationId={organisationId} />
    <Box sx={{ height: "1px", background: LINE }} />
    <AlarmButtonsSection organisationId={organisationId} />
  </Stack>
);

export default OrgSettingsTab;
